import { useCallback, useLayoutEffect, useState } from 'react'

import InlineScript from './InlineScript'

import { NavLinkScroll } from './NavLinkScroll'
import { LOGO_ALT, LOGO_SRC } from '../constants/branding'
import { applyTheme, readStoredTheme } from '../utils/themeApply'
import './navigation.css'

export default function Navigation() {
  const [theme, setTheme] = useState(() => readStoredTheme())

  useLayoutEffect(() => {
    applyTheme(theme)
  }, [theme])

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === 'light' ? 'dark' : 'light'))
  }, [])

  return (
    <div className="navigation-container1">
      <nav className="navigation-wrapper">
        <div className="navigation-container">
          <NavLinkScroll to="/">
            <div
              aria-label="Shiny Touch Painting & Renovation Ltd Home"
              className="navigation-brand"
            >
              <img
                src={LOGO_SRC}
                alt=""
                className="navigation-logo-img"
                width={44}
                height={44}
              />
              <span className="navigation-logo-text section-title">
                Shiny Touch
              </span>
            </div>
          </NavLinkScroll>
          <div className="navigation-desktop-menu">
            <ul className="navigation-links">
              <li>
                <NavLinkScroll to="/">
                  <div className="navigation-link">
                    <span>Home</span>
                  </div>
                </NavLinkScroll>
              </li>
              <li className="navigation-dropdown">
                <button
                  type="button"
                  aria-expanded="false"
                  aria-haspopup="true"
                  className="navigation-link navigation-dropdown-trigger"
                >
                  <span>Services</span>
                  <span className="navigation-icon-sm">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="none"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="m6 9l6 6l6-6"
                      ></path>
                    </svg>
                  </span>
                </button>
                <ul className="navigation-dropdown-menu">
                  <li>
                    <NavLinkScroll to="/services#service-interior-painting">
                      <div className="navigation-dropdown-link">
                        <span>Interior Painting</span>
                      </div>
                    </NavLinkScroll>
                  </li>
                  <li>
                    <NavLinkScroll to="/services#service-exterior-painting">
                      <div className="navigation-dropdown-link">
                        <span>Exterior Painting</span>
                      </div>
                    </NavLinkScroll>
                  </li>
                  <li>
                    <NavLinkScroll to="/services#service-renovation">
                      <div className="navigation-dropdown-link">
                        <span>Full Renovation</span>
                      </div>
                    </NavLinkScroll>
                  </li>
                  <li>
                    <NavLinkScroll to="/services#service-strata">
                      <div className="navigation-dropdown-link">
                        <span>Strata Maintenance</span>
                      </div>
                    </NavLinkScroll>
                  </li>
                </ul>
              </li>
              <li>
                <NavLinkScroll to="/before-after-gallery#project-gallery">
                  <div className="navigation-link">
                    <span>Gallery</span>
                  </div>
                </NavLinkScroll>
              </li>
              <li>
                <NavLinkScroll to="/about">
                  <div className="navigation-link">
                    <span>About</span>
                  </div>
                </NavLinkScroll>
              </li>
              <li>
                <NavLinkScroll to="/contact#quote-form">
                  <div className="navigation-link">
                    <span>Contact</span>
                  </div>
                </NavLinkScroll>
              </li>
            </ul>
          </div>
          <div className="navigation-actions">
            <button
              type="button"
              id="themeToggle"
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              className="navigation-theme-toggle"
              onClick={toggleTheme}
            >
              <span className="navigation-icon-theme navigation-icon-sun">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 2v2m2.837 12.385a6 6 0 1 1-7.223-7.222c.624-.147.97.66.715 1.248a4 4 0 0 0 5.26 5.259c.589-.255 1.396.09 1.248.715M16 12a4 4 0 0 0-4-4m7-3l-1.256 1.256M20 12h2"
                  ></path>
                </svg>
              </span>
            </button>
            <NavLinkScroll to="/request-a-free-quote#quick-quote-form">
              <div className="navigation-cta btn btn-primary btn-sm">
                <span>Free Estimate</span>
              </div>
            </NavLinkScroll>
            <button
              type="button"
              id="mobileMenuToggle"
              aria-label="Open menu"
              aria-expanded="false"
              className="navigation-mobile-toggle"
            >
              <span className="navigation-icon-menu">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  ></path>
                </svg>
              </span>
            </button>
          </div>
        </div>
      </nav>
      <div id="mobileOverlay" className="navigation-mobile-overlay">
        <div className="navigation-mobile-header">
          <NavLinkScroll to="/">
            <div className="navigation-brand">
              <img
                src={LOGO_SRC}
                alt=""
                className="navigation-logo-img"
                width={44}
                height={44}
              />
              <span className="navigation-logo-text section-title">
                Shiny Touch
              </span>
            </div>
          </NavLinkScroll>
          <button
            type="button"
            id="mobileMenuClose"
            aria-label="Close menu"
            className="navigation-mobile-close"
          >
            <span className="navigation-icon-close">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
              >
                <g
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                >
                  <rect width="18" height="18" x="3" y="3" rx="2"></rect>
                  <path d="M9 16l3-3l3 3"></path>
                </g>
              </svg>
            </span>
          </button>
        </div>
        <div className="navigation-mobile-content">
          <ul className="navigation-mobile-links">
            <li>
              <NavLinkScroll to="/">
                <div className="navigation-mobile-link">
                  <span>Home</span>
                </div>
              </NavLinkScroll>
            </li>
            <li>
              <details className="navigation-mobile-details">
                <summary className="navigation-mobile-link">
                  <span>Services</span>
                  <span className="navigation-icon-sm">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="none"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="m6 9l6 6l6-6"
                      ></path>
                    </svg>
                  </span>
                </summary>
                <ul className="navigation-mobile-sublinks">
                  <li>
                    <NavLinkScroll to="/services#service-interior-painting">
                      <div className="navigation-mobile-sublink">
                        <span>Interior Painting</span>
                      </div>
                    </NavLinkScroll>
                  </li>
                  <li>
                    <NavLinkScroll to="/services#service-exterior-painting">
                      <div className="navigation-mobile-sublink">
                        <span>Exterior Painting</span>
                      </div>
                    </NavLinkScroll>
                  </li>
                  <li>
                    <NavLinkScroll to="/services#service-renovation">
                      <div className="navigation-mobile-sublink">
                        <span>Full Renovation</span>
                      </div>
                    </NavLinkScroll>
                  </li>
                  <li>
                    <NavLinkScroll to="/services#service-strata">
                      <div className="navigation-mobile-sublink">
                        <span>Strata Maintenance</span>
                      </div>
                    </NavLinkScroll>
                  </li>
                </ul>
              </details>
            </li>
            <li>
              <NavLinkScroll to="/before-after-gallery#project-gallery">
                <div className="navigation-mobile-link">
                  <span>Gallery</span>
                </div>
              </NavLinkScroll>
            </li>
            <li>
              <NavLinkScroll to="/about">
                <div className="navigation-mobile-link">
                  <span>About</span>
                </div>
              </NavLinkScroll>
            </li>
            <li>
              <NavLinkScroll to="/contact#quote-form">
                <div className="navigation-mobile-link">
                  <span>Contact</span>
                </div>
              </NavLinkScroll>
            </li>
          </ul>
          <div className="navigation-mobile-footer">
            <NavLinkScroll to="/request-a-free-quote#quick-quote-form">
              <div className="navigation-mobile-cta btn btn-primary btn-lg">
                <span>Request a Free Quote</span>
              </div>
            </NavLinkScroll>
            <div className="navigation-mobile-info">
              <p className="section-content">
                99632 Market Crossing PO, Burnaby, BC
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="navigation-container2">
        <div className="navigation-container3">
          <InlineScript
            html={`<style>
@media (prefers-reduced-motion: reduce) {
.navigation-mobile-overlay, .navigation-dropdown-menu, .navigation-icon-sm svg {
  transition: none;
}
}
</style>`}
          ></InlineScript>
        </div>
      </div>
      <div className="navigation-container4">
        <div className="navigation-container5">
          <InlineScript
            html={`<script defer data-name="navigation-controls">
(function(){
  const mobileMenuToggle = document.getElementById("mobileMenuToggle")
  const mobileMenuClose = document.getElementById("mobileMenuClose")
  const mobileOverlay = document.getElementById("mobileOverlay")
  const body = document.body

  function openMenu() {
    mobileOverlay.classList.add("is-active")
    mobileMenuToggle.setAttribute("aria-expanded", "true")
    body.style.overflow = "hidden"
  }

  function closeMenu() {
    mobileOverlay.classList.remove("is-active")
    mobileMenuToggle.setAttribute("aria-expanded", "false")
    body.style.overflow = ""
  }

  if (mobileMenuToggle) mobileMenuToggle.addEventListener("click", openMenu)
  if (mobileMenuClose) mobileMenuClose.addEventListener("click", closeMenu)

  const mobileLinks = mobileOverlay ? mobileOverlay.querySelectorAll(".navigation-mobile-link:not(summary), .navigation-mobile-sublink") : []
  mobileLinks.forEach((link) => {
    link.addEventListener("click", closeMenu)
  })

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && mobileOverlay && mobileOverlay.classList.contains("is-active")) {
      closeMenu()
    }
  })

  const dropdownTriggers = document.querySelectorAll(".navigation-dropdown-trigger")
  dropdownTriggers.forEach((trigger) => {
    trigger.addEventListener("click", (e) => {
      const isExpanded = trigger.getAttribute("aria-expanded") === "true"
      trigger.setAttribute("aria-expanded", String(!isExpanded))
    })
  })
})()
</script>`}
          ></InlineScript>
        </div>
      </div>
    </div>
  )
}
